/* eslint-disable */
import React, { useEffect, useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faHome,
  faSync
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import { hasPermission } from "../../helpers/permission";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import moment from "moment";
import { addMonths, subMonths, startOfMonth, set } from "date-fns";
import '../../components/skeletonRow.css'


const FieldPermissionWrapper = ({ field, action = "view", children }) => {
  // Ensure hasPermission is imported correctly at the top
  const hasFieldPermission = hasPermission(field, action);
  return hasFieldPermission ? children : null;
};

const SubscriptionReport = () => {
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const tableRenderedRef = useRef(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);


  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // Separate state for form inputs and actual filters (like RevenueReport)
  const [formInputs, setFormInputs] = useState({
    fullname: "",
    amount: "",
    pay_date_start: null,
    pay_date_end: null,
    assignedto: ""
  });

  const [filters, setFilters] = useState({
    fullname: "",
    amount: "",
    pay_date_start: "",
    pay_date_end: "",
    assignedto: "",
    orderbycol: "created_on",
    orderby: "desc",
    page: 1,
    limit: 100
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "Showing 0 to 0 of 0 entries"
  });

  // Refs for double scroll
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Fetch assigned users for dropdown
  const fetchAssignedUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}report-subscription/assigned-users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*"
          }
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setAssignedUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching assigned users:", error);
      // showToast.error("Failed to load assigned users");
    }
  }, []);

  // Fetch subscription data
  const fetchSubscriptionData = useCallback(async (pageNumber = 1) => {
    try {
      setLoading(true);
      setIsRendering(true);
      tableRenderedRef.current = false;

      const token = localStorage.getItem("authToken");

      const payload = {
        fullname: filters.fullname || "",
        amount: filters.amount || "",
        pay_date_start: filters.pay_date_start ? moment(filters.pay_date_start).format("YYYY-MM-DD") : "",
        pay_date_end: filters.pay_date_end ? moment(filters.pay_date_end).format("YYYY-MM-DD") : "",
        assignedto: filters.assignedto || "",
        orderbycol: filters.orderbycol,
        orderby: filters.orderby,
        page: pageNumber - 1, // Backend expects 0-based page
        limit: filters.limit
      };

      // Remove empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await axios.post(
        `${API_URL}report-subscription`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data) {
        const data = response.data;
        setSubscriptionData(data.subscription || []);
        setTotalAmount(data.total_amount || 0);

        const totalRecords = data.total || 0;
        const totalPages = Math.ceil(totalRecords / filters.limit);
        const startRecord = ((pageNumber - 1) * filters.limit) + 1;
        const endRecord = Math.min(pageNumber * filters.limit, totalRecords);

        setPagination({
          currentPage: pageNumber,
          totalPages: totalPages,
          totalRecords: totalRecords,
          showingRecords: `${startRecord} to ${endRecord} of ${totalRecords}`
        });

        setTimeout(() => {
          tableRenderedRef.current = true;
          checkRenderingStatus();
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      setSubscriptionData([]);
      tableRenderedRef.current = true;
      checkRenderingStatus();
      showToast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  useEffect(() => {
    fetchSubscriptionData(filters.page);
  }, [fetchSubscriptionData]);

  useEffect(() => {
    fetchAssignedUsers();
  }, [fetchAssignedUsers]);

  // Double scroll implementation
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [subscriptionData]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  // Handle form input changes (without applying filters immediately) - like RevenueReport
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date range changes for Pay Date - like RevenueReport
  const handlePayDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs(prev => ({
      ...prev,
      pay_date_start: start,
      pay_date_end: end
    }));
  };

  // Apply all filters (called on Enter key or search button click) - like RevenueReport
  const applyFilters = () => {
    setFilters(prev => ({
      ...prev,
      fullname: formInputs.fullname,
      amount: formInputs.amount,
      pay_date_start: formInputs.pay_date_start,
      pay_date_end: formInputs.pay_date_end,
      assignedto: formInputs.assignedto,
      page: 1
    }));
  };

  // Handle Enter key in text inputs - like RevenueReport
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "asc" ? "desc" : "asc",
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const exportReport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        fullname: filters.fullname || "",
        amount: filters.amount || "",
        pay_date_start: filters.pay_date_start ? moment(filters.pay_date_start).format("YYYY-MM-DD") : "",
        pay_date_end: filters.pay_date_end ? moment(filters.pay_date_end).format("YYYY-MM-DD") : "",
        assignedto: filters.assignedto || "",
        orderbycol: filters.orderbycol,
        orderby: filters.orderby
      };

      // Remove empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await axios.post(
        `${API_URL}report-subscription/export`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          responseType: 'blob'
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const now = new Date();
      const timestamp = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}_${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}`;
      link.setAttribute('download', `subscription-report-${timestamp}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Report exported successfully");

    } catch (error) {
      console.error("Error exporting report:", error);
      showToast.error("Failed to export report");
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setFormInputs({
      fullname: "",
      amount: "",
      pay_date_start: null,
      pay_date_end: null,
      assignedto: ""
    });
    setFilters({
      fullname: "",
      amount: "",
      pay_date_start: "",
      pay_date_end: "",
      assignedto: "",
      orderbycol: "created_on",
      orderby: "desc",
      page: 1,
      limit: 100
    });
  };

  useEffect(() => {
    const handleClearFilters = () => {
      clearFilters();
    };

    window.addEventListener('clearSubscriptionReportFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearSubscriptionReportFiltersEvent', handleClearFilters);
    };
  }, []);

  // Helper functions
  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "1899-11-29T18:38:50.000Z") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === "0000-00-00 00:00:00") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return "";
    }
  };

  const hasAnyFilter = () => {
    return (
      filters.fullname ||
      filters.amount ||
      filters.pay_date_start ||
      filters.pay_date_end ||
      filters.assignedto ||
      filters.orderbycol !== "created_on" ||
      filters.orderby !== "desc"
    );
  };

  const isPrevDisabled = pagination.currentPage <= 1;
  const isNextDisabled = pagination.currentPage >= pagination.totalPages;

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      {/* Loading Spinner */}
      {/* {(loading || isRendering || exportLoading) && <LoadingSpinner />} */}

      {/* Content */}
      <div id="content" className="">
        {/* Breadcrumb and Controls - Updated like RevenueReport */}
        <div className="">
          <div className="panel-heading d-flex justify-content-between">
            <div className="col-md-4">
              <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
                {hasAnyFilter() && (
                  <button
                    className="btn btn-default btn-xs ml-1"
                    style={{ marginLeft: "6px" }}
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-3">
                {loading ? (
                  <span
                    className="skeleton-text"
                    style={{ width: "140px", display: "inline-block" }}
                  ></span>
                ) : (
                  pagination.showingRecords
                )}
              </span>

              <button
                className="btn btn-default btn-sm mx-1"
                onClick={() => fetchSubscriptionData(1)}
              >
                <FontAwesomeIcon icon={faSync} />
              </button>
              <button
                className="btn btn-default btn-sm mx-1"
                onClick={exportReport}
                disabled={exportLoading || subscriptionData.length === 0}
              >
                {exportLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faDownload} /> Export Report
                  </>
                )}
              </button>
              <button
                className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
                title="Previous"
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className={`btn btn-default btn-sm ${filters.page === pagination.totalPages ? "d-none" : ""}`}
                title="Next"
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page === pagination.totalPages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>

          {/* Main Panel */}
          <div className="panel panel-default with-rounded-corner with-shadow">
            {/* Double Scroll Container */}
            <div className="large-table-container-3">
              {/* Top Scrollbar */}
              <div
                ref={topScrollbarRef}
                onScroll={handleTopScroll}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "2px",
                  background: "#f8f9fa",
                }}
              >
                <div
                  ref={topScrollbarContentRef}
                  style={{ height: "1px" }}
                ></div>
              </div>

              {/* Table Container */}
              <div
                ref={tableContainerRef}
                onScroll={syncScroll}
                style={{
                  overflowY: "auto",
                  height: "calc(112vh - 250px)",
                  overflowX: "auto",
                  position: "relative",
                }}
              >
                <table className="table table-condensed table-hover bg-white sticky-table">
                  <thead>
                    {/* Header Row */}
                    <tr style={{ cursor: 'pointer' }}>
                      <FieldPermissionWrapper field="subcription_report_name">
                        <th onClick={() => handleSort('fullname')}>
                          Name <FontAwesomeIcon icon={getSortIcon('fullname')} />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_amount">
                        <th onClick={() => handleSort('amount')}>
                          Amount <FontAwesomeIcon icon={getSortIcon('amount')} />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_paydate">
                        <th style={{ minWidth: '155px' }} onClick={() => handleSort('pay_date')}>
                          Pay Date <FontAwesomeIcon icon={getSortIcon('pay_date')} />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_invoiceno">
                        <th onClick={() => handleSort('invoiceno')}>
                          Invoice No. <FontAwesomeIcon icon={getSortIcon('invoiceno')} />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_gstno">
                        <th>GST No.</th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_assignedto">
                        <th onClick={() => handleSort('assigned_user')}>
                          Assigned To <FontAwesomeIcon icon={getSortIcon('assigned_user')} />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="report_paymentmode">
                        <th>Payment Mode</th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="report_sendinvoice">
                        <th style={{ minWidth: '155px' }} onClick={() => handleSort('invoice_date')}>
                          Invoice Email Date <FontAwesomeIcon icon={getSortIcon('invoice_date')} />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_subscriptionid">
                        <th>Subscription ID</th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_status">
                        <th>Status</th>
                      </FieldPermissionWrapper>
                    </tr>

                    {/* Filter Row - Updated like RevenueReport */}
                    <tr>
                      <FieldPermissionWrapper field="subcription_report_name">
                        <th>
                          <input
                            type="text"
                            name="fullname"
                            placeholder="Search name..."
                            value={formInputs.fullname}
                            onChange={handleFormInputChange}
                            onKeyDown={handleKeyDown}
                            style={{
                              width: "120px",
                              padding: "5px",
                            }}
                          />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_amount">
                        <th>
                          <input
                            type="text"
                            name="amount"
                            placeholder="Amount..."
                            value={formInputs.amount}
                            onChange={handleFormInputChange}
                            onKeyDown={handleKeyDown}
                            style={{
                              width: "80px",
                              padding: "5px",
                            }}
                          />
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_paydate">
                        <th>
                          <div className="d-flex align-items-center gap-1">
                            <DatePicker
                              selected={formInputs.pay_date_start}
                              onChange={handlePayDateChange}
                              startDate={formInputs.pay_date_start}
                              endDate={formInputs.pay_date_end}
                              selectsRange
                              isClearable
                              toggleCalendarOnIconClick
                              placeholderText=""
                              monthsShown={3}
                              openToDate={formInputs.pay_date_start || new Date()}
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              scrollableYearDropdown
                              yearDropdownItemNumber={15}
                              minDate={new Date(2018, 0, 1)}
                              maxDate={new Date(2030, 11, 31)}
                              dateFormat="dd-MM-yyyy"
                            />
                            <button
                              type="button"
                              className="btn btn-default btn-xs btn-group-xs"
                              onClick={applyFilters}
                            >
                              <FontAwesomeIcon icon={faSearch} />
                            </button>
                          </div>
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_invoiceno">
                        <th><p>&nbsp;</p></th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_gstno">
                        <th><p>&nbsp;</p></th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_assignedto">
                        <th>
                          <select
                            name="assignedto"
                            value={formInputs.assignedto}
                            onChange={(e) => {
                              const value = e.target.value;

                              // update form input
                              setFormInputs(prev => ({
                                ...prev,
                                assignedto: value
                              }));

                              // apply filter immediately
                              setFilters(prev => ({
                                ...prev,
                                assignedto: value,
                                page: 1
                              }));
                            }}
                            style={{ width: "120px", padding: "5px" }}
                          >

                            <option value="">--select--</option>
                            {assignedUsers.map((user) => (
                              <option key={user.id} value={user.t5_id}>
                                {user.t5_user_fullname || user.fullname}
                              </option>
                            ))}
                          </select>
                        </th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="report_paymentmode">
                        <th><p>&nbsp;</p></th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="report_sendinvoice">
                        <th><p>&nbsp;</p></th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_subscriptionid">
                        <th><p>&nbsp;</p></th>
                      </FieldPermissionWrapper>

                      <FieldPermissionWrapper field="subcription_report_status">
                        <th><p>&nbsp;</p></th>
                      </FieldPermissionWrapper>
                    </tr>
                  </thead>
                  <tbody>
                    {(loading || isRendering) ? (
                      // Skeleton loading rows (initial load + filter apply)
                      Array.from({ length: 10 }).map((_, skeletonIndex) => (
                        <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                        </tr>
                      ))
                    ) : subscriptionData.length > 0 ? (
                      subscriptionData.map((item, idx) => (
                        <tr key={idx} style={{ cursor: 'pointer' }}>
                          <FieldPermissionWrapper field="subcription_report_name">
                            <td>{item.subscriberfullname || item.fullname}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_amount">
                            <td className="ctc">{item.amount || '0.00'}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_paydate">
                            <td className="ctc">{formatDate(item.pay_date)}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_invoiceno">
                            <td className="ctc">{item.invoiceno}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_gstno">
                            <td className="ctc">{item.gstin}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_assignedto">
                            <td className="ctc">{item.assigned_user || item.t2_assignedto}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="report_paymentmode">
                            <td className="ctc">{item.pay_mode}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="report_sendinvoice">
                            <td className="ctc">{formatDateTime(item.invoice_date)}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_subscriptionid">
                            <td className="ctc">{item.subscriptionid}</td>
                          </FieldPermissionWrapper>

                          <FieldPermissionWrapper field="subcription_report_status">
                            <td className="ctc">{item.status}</td>
                          </FieldPermissionWrapper>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        {/* <td colSpan="12" className="text-center py-4">
                          {loading ? 'Loading...' : 'No subscription records found'}
                        </td> */}
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                      {/* Name column */}
                      <FieldPermissionWrapper field="subcription_report_name">
                        <td>Total</td>
                      </FieldPermissionWrapper>

                      {/* Amount column */}
                      <FieldPermissionWrapper field="subcription_report_amount">
                        <td className="ctc">
                          {Number(totalAmount).toFixed(2)}
                        </td>
                      </FieldPermissionWrapper>

                      {/* Remaining columns empty */}
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .loading {
          position: fixed;
          z-index: 2050;
          height: 2em;
          width: 2em;
          overflow: show;
          margin: auto;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
        }

        .loading:before {
          content: '';
          display: block;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(rgba(20, 20, 20,.8), rgba(0, 0, 0, .8));
          background: -webkit-radial-gradient(rgba(20, 20, 20,.8), rgba(0, 0, 0,.8));
        }

        .loading:not(:required) {
          font: 0/0 a;
          color: transparent;
          text-shadow: none;
          background-color: transparent;
          border: 0;
        }

        .loading:not(:required):after {
          content: '';
          display: block;
          font-size: 10px;
          width: 1em;
          height: 1em;
          margin-top: -0.5em;
          -webkit-animation: spinner 150ms infinite linear;
          -moz-animation: spinner 150ms infinite linear;
          -ms-animation: spinner 150ms infinite linear;
          -o-animation: spinner 150ms infinite linear;
          animation: spinner 150ms infinite linear;
          border-radius: 0.5em;
          -webkit-box-shadow: rgba(255,255,255, 0.75) 1.5em 0 0 0, rgba(255,255,255, 0.75) 1.1em 1.1em 0 0, rgba(255,255,255, 0.75) 0 1.5em 0 0, rgba(255,255,255, 0.75) -1.1em 1.1em 0 0, rgba(255,255,255, 0.75) -1.5em 0 0 0, rgba(255,255,255, 0.75) -1.1em -1.1em 0 0, rgba(255,255,255, 0.75) 0 -1.5em 0 0, rgba(255,255,255, 0.75) 1.1em -1.1em 0 0;
          box-shadow: rgba(255,255,255, 0.75) 1.5em 0 0 0, rgba(255,255,255, 0.75) 1.1em 1.1em 0 0, rgba(255,255,255, 0.75) 0 1.5em 0 0, rgba(255,255,255, 0.75) -1.1em 1.1em 0 0, rgba(255,255,255, 0.75) -1.5em 0 0 0, rgba(255,255,255, 0.75) -1.1em -1.1em 0 0, rgba(255,255,255, 0.75) 0 -1.5em 0 0, rgba(255,255,255, 0.75) 1.1em -1.1em 0 0;
        }

        @-webkit-keyframes spinner {
          0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); }
          100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); }
        }
        @-moz-keyframes spinner {
          0% { -moz-transform: rotate(0deg); transform: rotate(0deg); }
          100% { -moz-transform: rotate(360deg); transform: rotate(360deg); }
        }
        @-o-keyframes spinner {
          0% { -o-transform: rotate(0deg); transform: rotate(0deg); }
          100% { -o-transform: rotate(360deg); transform: rotate(360deg); }
        }
        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        .form-control.input-sm {
          height: 28px;
          padding: 3px 6px;
          font-size: 12px;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .panel-content {
          padding: 15px;
        }

        .panel-heading {
          padding: 10px 15px;
          border-bottom: 1px solid #eee;
          background: #f8f9fa;
        }

        .large-table-container-3 {
          position: relative;
          overflow: hidden;
        }

        .sticky-table {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionReport;